import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  useTheme,
  Tooltip,
  Skeleton,
  Alert,
  Fade,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Edit as EditIcon, 
  Mic as MicIcon,
  ArrowForward,
  AccessTime,
  Psychology,
} from '@mui/icons-material';

// Memoized option card component to reduce re-renders
const OptionCard = React.memo(({
  icon: Icon,
  title,
  description,
  features,
  route,
  color,
  isLoading,
  onNavigate,
  index,
}) => {
  const theme = useTheme();
  
  const handleClick = useCallback(() => {
    onNavigate(route);
  }, [onNavigate, route]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <Grid item xs={12} md={6}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
      >
        <Paper
          component={motion.div}
          whileHover={{ 
            scale: 1.02,
            boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
          }}
          whileTap={{ scale: 0.98 }}
          sx={{
            p: 4,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            borderRadius: 3,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '2px solid transparent',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              borderColor: color,
            },
            '&:focus-visible': {
              outline: `3px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
            },
            // Add subtle gradient background
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${color}08 100%)`,
          }}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-label={`Choose ${title} method for CV creation`}
          aria-describedby={`${route}-description`}
        >
          {/* Animated background effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, ${color}05, transparent)`,
              opacity: 0,
              transition: 'opacity 0.3s ease',
              '.MuiPaper-root:hover &': {
                opacity: 1,
              },
            }}
          />
          
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              position: 'relative',
              zIndex: 1,
              boxShadow: `0 8px 16px ${color}40`,
            }}
          >
            <Icon sx={{ fontSize: 48, color: 'white' }} />
          </Box>
          
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 2, zIndex: 1 }}>
            {title}
          </Typography>
          
          <Typography 
            id={`${route}-description`}
            color="text.secondary" 
            sx={{ mb: 3, lineHeight: 1.6, zIndex: 1 }}
          >
            {description}
          </Typography>

          {/* Feature list */}
          <Box sx={{ mb: 3, zIndex: 1 }}>
            {features.map((feature, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <feature.icon sx={{ fontSize: 16, color: color, mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ mt: 'auto', pt: 2, zIndex: 1 }}>
            <Button 
              variant="contained" 
              size="large"
              endIcon={isLoading ? null : <ArrowForward />}
              disabled={isLoading}
              sx={{ 
                px: 4, 
                py: 1.5,
                fontWeight: 'bold',
                backgroundColor: color,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: color,
                  filter: 'brightness(0.9)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? <Skeleton width={120} height={24} /> : `Start ${title.split(' ')[0]}`}
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </Grid>
  );
});

OptionCard.displayName = 'OptionCard';

const ModeSelector = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleNavigation = useCallback(async (route) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Add slight delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      navigate(route);
    } catch (err) {
      setError('Navigation failed. Please try again.');
      console.error('Navigation error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const options = [
    {
      icon: EditIcon,
      title: 'Fill Form Manually',
      description: 'Step through a guided form process, filling in each section of your CV with detailed information and professional formatting.',
      features: [
        { icon: AccessTime, text: '15-20 minutes to complete' },
        { icon: EditIcon, text: 'Section-by-section guidance' },
        { icon: ArrowForward, text: 'Full control over content' },
      ],
      route: '/manual',
      color: theme.palette.primary.main,
    },
    {
      icon: MicIcon,
      title: 'Voice Recording',
      description: 'Simply speak your entire CV details naturally. Our AI will intelligently process and structure your information into a professional CV.',
      features: [
        { icon: MicIcon, text: '5-10 minutes speaking time' },
        { icon: Psychology, text: 'AI-powered processing' },
        { icon: ArrowForward, text: 'Natural conversation flow' },
      ],
      route: '/voice',
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 4, md: 8 },
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography 
            variant="h3" 
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            Choose Your CV Creation Method
          </Typography>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Select the approach that works best for you. Both methods create professional, ATS-optimized CVs.
          </Typography>
        </motion.div>
      </Box>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert 
              severity="error" 
              sx={{ mb: 4 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Options Grid */}
      <Grid container spacing={4}>
        {options.map((option, index) => (
          <OptionCard
            key={option.route}
            {...option}
            index={index}
            isLoading={isLoading}
            onNavigate={handleNavigation}
          />
        ))}
      </Grid>

      {/* Help Section */}
      <Fade in timeout={1000}>
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Need help deciding? 
            <Tooltip title="The manual form gives you complete control, while voice input is faster and more natural">
              <Button
                variant="text"
                size="small"
                sx={{ ml: 1, textTransform: 'none' }}
              >
                Compare methods
              </Button>
            </Tooltip>
          </Typography>
        </Box>
      </Fade>
    </Container>
  );
};

export default ModeSelector; 